import { useCallback, useEffect, useMemo, useState } from 'react'
import { C33Column, COLUMNS, LOCKED_IDS } from './columns'

// Bumped whenever the column set changes shape. A saved layout from an older
// version is discarded rather than half-applied, so a removed column can never
// come back from localStorage.
const STORAGE_KEY = 'c33.table.v1'

export const MIN_COL_W = 56
export const MAX_COL_W = 420

export interface TablePrefs {
  order: string[]
  widths: Record<string, number>
  hidden: string[]
}

export function defaultPrefs(columns: C33Column[] = COLUMNS): TablePrefs {
  return { order: columns.map(c => c.id), widths: {}, hidden: [] }
}

const lockedCount = (columns: C33Column[]) => columns.filter(c => c.locked).length

// Reconciles a persisted layout against the columns that actually exist now:
// unknown ids are dropped, columns added since the save are appended in their
// default position, and locked columns are forced back to the front.
export function sanitize(raw: unknown, columns: C33Column[] = COLUMNS): TablePrefs {
  const base = defaultPrefs(columns)
  if (!raw || typeof raw !== 'object') return base
  const saved = raw as Partial<TablePrefs>
  const known = new Set(columns.map(c => c.id))

  const locked = columns.filter(c => c.locked).map(c => c.id)
  const seen = new Set<string>(locked)
  const order = [...locked]
  for (const id of Array.isArray(saved.order) ? saved.order : []) {
    if (known.has(id) && !seen.has(id)) {
      seen.add(id)
      order.push(id)
    }
  }
  // columns introduced since the layout was saved
  for (const c of columns) if (!seen.has(c.id)) order.push(c.id)

  const widths: Record<string, number> = {}
  const savedWidths = saved.widths && typeof saved.widths === 'object' ? saved.widths : {}
  for (const [id, w] of Object.entries(savedWidths)) {
    if (known.has(id) && typeof w === 'number' && Number.isFinite(w)) {
      widths[id] = Math.min(MAX_COL_W, Math.max(MIN_COL_W, w))
    }
  }

  const hidden = (Array.isArray(saved.hidden) ? saved.hidden : []).filter(
    id => known.has(id) && !LOCKED_IDS.includes(id)
  )

  return { order, widths, hidden }
}

// Moves `dragId` so it sits where `targetId` currently is. Locked columns never
// move, and nothing may be dropped into the slots they occupy.
export function reorder(
  prefs: TablePrefs,
  dragId: string,
  targetId: string,
  columns: C33Column[] = COLUMNS
): TablePrefs {
  const locked = lockedCount(columns)
  if (dragId === targetId) return prefs
  if (LOCKED_IDS.includes(dragId) || LOCKED_IDS.includes(targetId)) return prefs

  const from = prefs.order.indexOf(dragId)
  const to = prefs.order.indexOf(targetId)
  if (from < 0 || to < 0 || to < locked) return prefs

  const order = [...prefs.order]
  order.splice(from, 1)
  order.splice(to, 0, dragId)
  return { ...prefs, order }
}

export function toggleHidden(prefs: TablePrefs, id: string): TablePrefs {
  if (LOCKED_IDS.includes(id)) return prefs
  const hidden = prefs.hidden.includes(id)
    ? prefs.hidden.filter(h => h !== id)
    : [...prefs.hidden, id]
  return { ...prefs, hidden }
}

export function setWidth(prefs: TablePrefs, id: string, width: number): TablePrefs {
  if (!Number.isFinite(width)) return prefs
  const clamped = Math.min(MAX_COL_W, Math.max(MIN_COL_W, Math.round(width)))
  return { ...prefs, widths: { ...prefs.widths, [id]: clamped } }
}

export function clearWidth(prefs: TablePrefs, id: string): TablePrefs {
  const widths = { ...prefs.widths }
  delete widths[id]
  return { ...prefs, widths }
}

export function widthOf(prefs: TablePrefs, col: C33Column): number {
  return prefs.widths[col.id] ?? col.width
}

function load(columns: C33Column[]): TablePrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return sanitize(raw ? JSON.parse(raw) : null, columns)
  } catch {
    // private mode, quota, or corrupt JSON — a broken layout must never stop
    // the table from rendering
    return defaultPrefs(columns)
  }
}

export function useTablePrefs(columns: C33Column[] = COLUMNS) {
  const [prefs, setPrefs] = useState<TablePrefs>(() => load(columns))

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
    } catch {
      /* not being able to persist is not worth surfacing */
    }
  }, [prefs])

  const reset = useCallback(() => {
    setPrefs(defaultPrefs(columns))
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* nothing to clean up */
    }
  }, [columns])

  // the columns actually rendered, in the user's order, minus hidden ones
  const visibleColumns = useMemo(() => {
    const byId = new Map(columns.map(c => [c.id, c]))
    return prefs.order
      .map(id => byId.get(id))
      .filter((c): c is C33Column => !!c && !prefs.hidden.includes(c.id))
  }, [columns, prefs])

  const isDirty = useMemo(() => {
    const d = defaultPrefs(columns)
    return (
      prefs.hidden.length > 0 ||
      Object.keys(prefs.widths).length > 0 ||
      prefs.order.join() !== d.order.join()
    )
  }, [columns, prefs])

  return { prefs, setPrefs, reset, visibleColumns, isDirty }
}
